/**
 * `Promise.allSettled` with a ceiling on how many run at once.
 *
 * The task API has no bulk endpoint, so a bulk action is N single-id PATCHes,
 * and every one of them runs its own serializable transaction. Those conflict
 * with each other even across different tasks -- SSI takes predicate locks, and
 * on a table this size they overlap -- so the limit is not about politeness to
 * the network, it is what decides whether the writes land at all. Measured
 * against eight tasks on the real database: 8/8 succeed run one at a time, 6/8
 * at two, 4/8 at six. Hence the caller's ceiling of one.
 *
 * Results come back in the order of `items`, not completion order, so a caller
 * can pair each one with the row it came from by index.
 */
export async function mapPool<T, R>(
  items: readonly T[],
  limit: number,
  fn: (item: T) => Promise<R>,
): Promise<PromiseSettledResult<R>[]> {
  const results = new Array<PromiseSettledResult<R>>(items.length);
  let cursor = 0;

  // Each worker pulls the next index until they run out, rather than the list
  // being sliced into fixed chunks up front: one slow request would otherwise
  // hold up its whole chunk while other workers sat idle.
  await Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, async () => {
      while (cursor < items.length) {
        const index = cursor++;
        try {
          results[index] = { status: 'fulfilled', value: await fn(items[index]!) };
        } catch (reason) {
          results[index] = { status: 'rejected', reason };
        }
      }
    }),
  );

  return results;
}
